<script lang="ts">
  import { BULK_SALES_CHANNELS } from '@domain/bulk-sales/model';
  import type { LawyerBulkSalesDecision } from '@domain/bulk-sales/model';
  import {
    BULK_SALES_CHANNEL_LABELS,
    BULK_SALES_SOURCE_LABELS,
    BULK_SALES_STATE_LABELS,
    lawyerBulkSalesSources,
  } from '@domain/bulk-sales/labels';

  /**
   * 弁護士詳細「現在の判定」の read-only 表示 (spec 20260716「弁護士詳細」)。編集 UI
   * (BulkSalesChannelEditor) と同じ feature に置く (widgets 同士の import は FSD で禁止)。
   * backend の派生値 (本人 / 現所属事務所 / 懲戒の OR 合成) をそのまま描画する:
   * 全体 3 値 → チャネル別 3 値 → 由来 (複数はすべて)。経路で結果が分かれる
   * チャネルは「一部対象外」になる。合成はここで再実装しない。
   */
  interface Props {
    decision: LawyerBulkSalesDecision;
  }

  let { decision }: Props = $props();

  const overallSources = $derived(lawyerBulkSalesSources(decision));

  function sourcesLabel(sources: readonly ('lawyer' | 'firm' | 'disciplinary')[]): string {
    return sources.map((s) => BULK_SALES_SOURCE_LABELS[s]).join('・');
  }
</script>

<div class="breakdown" data-testid="bulk-sales-decision-breakdown">
  <div class="overall">
    <span
      class="state-badge"
      class:state-partial={decision.state === 'partially_excluded'}
      class:state-excluded={decision.state === 'excluded'}
      data-testid="bulk-sales-overall-state"
    >
      一括営業{BULK_SALES_STATE_LABELS[decision.state]}
    </span>
    {#if overallSources.length > 0}
      <span class="muted small">由来: {sourcesLabel(overallSources)}</span>
    {/if}
  </div>
  <ul class="channels">
    {#each BULK_SALES_CHANNELS as channel (channel)}
      {@const c = decision.channels[channel]}
      <li class="channel" data-channel={channel}>
        <span class="channel-name">{BULK_SALES_CHANNEL_LABELS[channel]}</span>
        <span
          class="state-badge"
          class:state-partial={c.state === 'partially_excluded'}
          class:state-excluded={c.state === 'excluded'}
        >
          {BULK_SALES_STATE_LABELS[c.state]}
        </span>
        {#if c.sources.length > 0}
          <span class="muted small">{sourcesLabel(c.sources)}</span>
        {/if}
      </li>
    {/each}
  </ul>
</div>

<style>
  .breakdown {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .overall {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .channels {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
  }
  .channel {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
  }
  .channel-name {
    color: #374151;
  }
  .state-badge {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1.35;
    padding: 2px 8px;
    background: #f3f4f6;
    color: #4b5563;
  }
  .state-badge.state-partial {
    background: #fef3c7;
    color: #92400e;
  }
  .state-badge.state-excluded {
    background: #fee2e2;
    color: #991b1b;
  }
  .muted {
    color: #6b7280;
  }
  .small {
    font-size: 0.75rem;
  }
</style>
